import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../hooks/useNotifications';
import { Button } from '../components/UI';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'token' | 'success'>('email');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const notifications = useNotifications();
  
  // Form states
  const [emailForm, setEmailForm] = useState({
    email: ''
  });
  
  const [tokenForm, setTokenForm] = useState({
    token: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false
  });

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailForm({
      ...emailForm,
      [e.target.name]: e.target.value
    });
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTokenForm({
      ...tokenForm,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post('/auth/forgot-password', emailForm);
      
      setMessage({
        type: 'success',
        text: response.data.message
      });
      
      notifications.success.emailSent();
      
      // Se retornou token (modo desenvolvimento), ir para próxima etapa
      if (response.data.resetToken) {
        setStep('token');
        setMessage({
          type: 'success',
          text: `Token gerado: ${response.data.resetToken}`
        });
      }
      
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao solicitar reset de senha'
      });
      notifications.error.emailSend(error.response?.data?.message || t('forgotPassword.requestError'));
    } finally {
      setLoading(false);
    }
  };

  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (tokenForm.newPassword !== tokenForm.confirmPassword) {
      setMessage({
        type: 'error',
        text: t('forgotPassword.passwordMismatch')
      });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('/auth/reset-password-token', tokenForm);
      
      setMessage({
        type: 'success',
        text: response.data.message
      });
      
      notifications.success.passwordChanged();
      setStep('success');
      
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || t('forgotPassword.resetError')
      });
      notifications.error.passwordChange(error.response?.data?.message || t('forgotPassword.resetError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
            <Shield className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {t('forgotPassword.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {step === 'email' && t('forgotPassword.stepEmail')}
            {step === 'token' && t('forgotPassword.stepToken')}
            {step === 'success' && t('forgotPassword.stepSuccess')}
          </p>
        </div>

        {/* Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-lg">
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('forgotPassword.email')}
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={emailForm.email}
                    onChange={handleEmailChange}
                    className="field pl-10 pr-3"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <Button className="w-full"
                type="submit"
                disabled={loading}>
                {loading ? t('common.sending') : t('forgotPassword.request')}
              </Button>
            </form>
          )}

          {step === 'token' && (
            <form onSubmit={handleTokenSubmit} className="space-y-6">
              <div>
                <label htmlFor="token" className="block text-sm font-medium text-gray-700">
                  {t('forgotPassword.token')}
                </label>
                <input
                  id="token"
                  name="token"
                  type="text"
                  required
                  value={tokenForm.token}
                  onChange={handleTokenChange}
                  className="field field--lg mt-1"
                  placeholder="Cole o token aqui"
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  {t('forgotPassword.newPassword')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showPasswords.newPassword ? 'text' : 'password'}
                    required
                    value={tokenForm.newPassword}
                    onChange={handleTokenChange}
                    className="field field--lg"
                    placeholder="Mínimo 8 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('newPassword')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.newPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirmar {t('forgotPassword.newPassword')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPasswords.confirmPassword ? 'text' : 'password'}
                    required
                    value={tokenForm.confirmPassword}
                    onChange={handleTokenChange}
                    className="field field--lg"
                    placeholder="Confirme a nova senha"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.confirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="flex space-x-3">
                <Button variant="ghost" className="flex-1"
                  type="button"
                  onClick={() => setStep('email')}>
                  {t('common.back')}
                </Button>
                <Button className="flex-1"
                  type="submit"
                  disabled={loading}>
                  {loading ? t('common.sending') : t('forgotPassword.reset')}
                </Button>
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center space-y-6">
              <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{t('forgotPassword.successTitle')}</h3>
                <p className="mt-2 text-sm text-gray-600">
                  {t('forgotPassword.successMsg')}
                </p>
              </div>
              <Button className="w-full"
                onClick={() => navigate('/login')}>
                {t('forgotPassword.goLogin')}
              </Button>
            </div>
          )}

          {/* Messages */}
          {message && (
            <div className={`mt-4 p-4 rounded-lg flex items-center space-x-2 ${
              message.type === 'success' 
                ? 'bg-green-100 border border-green-400 text-green-700' 
                : 'bg-red-100 border border-red-400 text-red-700'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertTriangle className="h-5 w-5" />
              )}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {/* Emergency Reset removido em produção */}
        </div>

        {/* Footer */}
        <div className="text-center">
          <Link 
            to="/login" 
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('forgotPassword.goLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
